/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { memo, ReactElement, useCallback } from 'react';
import RequestType from '@Types/common/RequestTypes';
import { useUserPermissions } from '@Hooks/index';
import RequestList, { RequestListProps } from './RequestListDesktop';
import { TableManageRequestsActions } from '../tableActions';

export interface ManageRequestListProps
  extends Omit<RequestListProps, 'renderActionColumn'> {
  requestType: RequestType;
  onEdit?: (recordKey: string) => void;
  onSubmit?: (requestNo: string) => void;
  onView?: (recordKey: string) => void;
  onCancel?: (recordKey: string) => void;
  onDelete?: (requestNo: string) => void;
  onApprovalHistory?: (requestNo: string, isIt?: boolean) => void;
  onCopy?: (requestNo: string, mrNo: string) => void;
  ownActions?: ReactElement | null;
  showAction?: boolean;
  hidePagination?: boolean;
  isFiltersApplied?: boolean;
  isSearchApplied?: boolean;
}

const ManageRequestList: React.FC<ManageRequestListProps> = (
  props
): JSX.Element => {
  const {
    showAction = true,
    onEdit,
    onSubmit,
    onView,
    onCancel,
    onCopy,
    onDelete,
    onApprovalHistory,
    hidePagination = false,
    isFiltersApplied = false,
    isSearchApplied = false,
    requestType,
    ownActions,
    ...restProps
  } = props;

  const permissions = useUserPermissions();

  const renderActionColumn = useCallback(
    (_text: any, record: any) => {
      if (ownActions) {
        const elementWithProps = React.cloneElement(ownActions, {
          record,
        });
        return elementWithProps;
      }
      return (
        <TableManageRequestsActions
          status={record.status}
          requestNo={record.requestNo}
          requestType={requestType}
          permissions={permissions}
          onEdit={() => onEdit?.(record.key)}
          onSubmit={() => onSubmit?.(record.key)}
          onView={() => onView?.(record.key)}
          onCancel={() => onCancel?.(record.key)}
          onCopy={() => onCopy?.(record.key, record.requestNo)}
          onDelete={() => onDelete?.(record.key)}
          onApprovalHistory={() => onApprovalHistory?.(record.key)}
        />
      );
    },
    [onEdit, onSubmit, onView, onDelete, ownActions]
  );

  return (
    <RequestList
      {...restProps}
      isFiltersApplied={isFiltersApplied}
      isSearchApplied={isSearchApplied}
      hidePagination={hidePagination}
      renderActionColumn={showAction ? renderActionColumn : undefined}
    />
  );
};

export default memo(ManageRequestList) as typeof ManageRequestList;
